"use client"

import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { cn } from "src/lib/utils"
import {
  yoolColors,
  yoolBorders,
  yoolTypography,
  yoolShadows,
} from "src/styles/tokens"

// ─── Tokens de Tabs Yool ──────────────────────────────────────────────────────
const TABS = {
  list: {
    bg:           yoolColors.gray,          // #EBEBEB
    borderRadius: yoolBorders.radius.sm,    // 8px
    padding:      '4px',
    height:       '40px',
    gap:          '2px',
  },
  trigger: {
    borderRadius: '6px',
    paddingX:     '14px',
    paddingY:     '6px',
    fontSize:     '13px',
    fontWeight:   600,
    transition:   'background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
    minHeight:    '32px',
    inactive: {
      bg:    'transparent',
      color: yoolColors.muted,              // #8D8D8D
    },
    hover: {
      bg:    yoolColors.white,
      color: yoolColors.gray1,             // #54585A
    },
    active: {
      bg:     yoolColors.white,
      color:  yoolColors.cta,              // #007D6E — indica selección activa
      shadow: yoolShadows.sm,
    },
  },
  content: {
    marginTop: '16px',
  },
}

// ─── Tabs Root — Radix puro, sin cambios ─────────────────────────────────────
const Tabs = TabsPrimitive.Root

// ─── TabsList ────────────────────────────────────────────────────────────────
const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn('', className)}
    style={{
      display:         'inline-flex',
      alignItems:      'center',
      justifyContent:  'flex-start',
      height:          TABS.list.height,
      borderRadius:    TABS.list.borderRadius,
      backgroundColor: TABS.list.bg,
      padding:         TABS.list.padding,
      gap:             TABS.list.gap,
    }}
    {...props}
  />
))
TabsList.displayName = 'TabsList'

// ─── TabsTrigger ─────────────────────────────────────────────────────────────
const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const internalRef = React.useRef(null)
  const resolvedRef = ref || internalRef

  const [isActive,  setIsActive]  = React.useState(false)
  const [isHovered, setIsHovered] = React.useState(false)
  const [isFocused, setIsFocused] = React.useState(false)

  // Sincronizar con data-state de Radix via MutationObserver
  React.useEffect(() => {
    const el = resolvedRef.current
    if (!el) return
    setIsActive(el.getAttribute('data-state') === 'active')
    const obs = new MutationObserver(() => {
      setIsActive(el.getAttribute('data-state') === 'active')
    })
    obs.observe(el, { attributes: true, attributeFilter: ['data-state'] })
    return () => obs.disconnect()
  }, [])

  const stateStyles = isActive
    ? {
        backgroundColor: TABS.trigger.active.bg,
        color:           TABS.trigger.active.color,
        boxShadow:       TABS.trigger.active.shadow,
      }
    : isHovered
    ? {
        backgroundColor: TABS.trigger.hover.bg,
        color:           TABS.trigger.hover.color,
        boxShadow:       'none',
      }
    : {
        backgroundColor: TABS.trigger.inactive.bg,
        color:           TABS.trigger.inactive.color,
        boxShadow:       'none',
      }

  return (
    <TabsPrimitive.Trigger
      ref={resolvedRef}
      className={cn('', className)}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        whiteSpace:     'nowrap',
        borderRadius:   TABS.trigger.borderRadius,
        padding:        TABS.trigger.paddingY + ' ' + TABS.trigger.paddingX,
        fontSize:       TABS.trigger.fontSize,
        fontWeight:     TABS.trigger.fontWeight,
        transition:     TABS.trigger.transition,
        cursor:         'pointer',
        border:         'none',
        outline:        isFocused ? ('2px solid ' + yoolColors.cta) : 'none',
        outlineOffset:  '2px',
        minHeight:      TABS.trigger.minHeight,
        ...stateStyles,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={()    => setIsFocused(true)}
      onBlur={()     => setIsFocused(false)}
      {...props}
    />
  )
})
TabsTrigger.displayName = 'TabsTrigger'

// ─── TabsContent ─────────────────────────────────────────────────────────────
const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('', className)}
    style={{
      marginTop: TABS.content.marginTop,
      outline:   'none',
    }}
    {...props}
  />
))
TabsContent.displayName = 'TabsContent'

export { Tabs, TabsList, TabsTrigger, TabsContent }